import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, UserPlus } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  DataTable,
  FilterBar,
  FilterChip,
  SelectChip,
  SplitList,
  StatusChip,
  type IDataColumn
} from '@/design';
import { useCan, type AccountKind } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { accountsApi } from '../api/accounts-api';
import {
  ACCOUNT_KINDS,
  ACCOUNT_STATUSES,
  type AccountStatus,
  type IAccountQuery,
  type IAdminAccountRow
} from '../api/account-contracts';
import { ACCOUNT_KIND_WORDS, AccountKindChip, AccountRoleChips } from '../components/account-facts';
import { useRoleList, useUsersAndRolesTabs } from '../components/account-roles';
import type { IPage } from '../api/oversight-contracts';
import {
  AdminPage,
  LoadError,
  Pager,
  PastTheEnd,
  pageOf,
  useAdminData,
  withPage
} from '../components/admin-page';
import { ListArea, usePaneRead } from '../components/list-area';
import type { ICardLinkState } from '../components/participant-link';
import { TEAM_PATHS, userPath } from '../paths';
import { refusalMessage } from '../refusal';
import { CreateAccountCard } from './create-account-card';

/**
 * Who can log in — `GET /api/v1/admin/accounts`, under `accounts.browse`, which is also what
 * opens this screen (`AdminRoute`, the rail).
 *
 * **Each account is a kind and its roles** (SR-228): a chip saying which portal it signs in to
 * ("Advisor Portal" / "Team Portal") and its Team Portal roles by name. The Role filter offers
 * every role from `GET /admin/roles` where the reader holds `roles.manage`, and otherwise the
 * roles drawn on the page — no read the account would be refused; a role named in the URL and
 * known to neither is sent and drawn as spelled.
 *
 * **The filters are the URL** — `email`, `name`, `kind`, `roleId`, `status` and `page` — as on
 * every other Team Portal list, so an account's card opened from the list is drawn beside it
 * (`AdminUsersArea`) and the way back restores the list. Until the two portals they were the
 * component's state, typed into and sent a moment after each keystroke; they are submitted now,
 * as every list's search is, and a value the URL names is sent as spelled.
 *
 * ## Two search boxes, not one
 *
 * The old fixture had a single box claiming to search "email, role or participant", which it
 * could do because it held every row. The endpoint has two substring filters — `?email=`
 * over the address and `?name=` over the display names of the participants an account acts
 * for — and it **ANDs** them. One box feeding both would look like a wider search and
 * actually be a narrower one: `smith` would ask for an address containing `smith` *whose
 * participant* also contains it, and almost always find nothing. So they are two fields, and
 * each says what it matches: the bar's box is the address, and "Speaks for" the names.
 *
 * ## Active accounts by default
 *
 * A disabled account (SR-442) is hidden unless the Status filter says otherwise (SR-421's AC2): a
 * URL naming no `status` sends `status=active`, as survey-api's design asks of a client that wants
 * the active ones — its own default lists every account (D6). "Every status" is `status=all`, a
 * word of this page's that survey-api's `status` has no value for, sent as no `status` at all; any
 * other value is sent as spelled. The chip draws the default as its first position, as the
 * compliance queue draws Outstanding, so Clear goes back to it.
 *
 * ## What is not here
 *
 * No **last sign-in** column: the row carries `createdAt` and `updatedAt`, and the newest
 * `auth_session` is not projected. The fixture had one, which is worth naming as a
 * prediction that did not hold rather than as a column someone forgot. The two instants the row
 * does carry are the column picker's to add.
 *
 * The order is fixed at `createdAt DESC` and there is no sortable header, deliberately: a
 * paged read resolves its order in SQL, and a name ordering there is collation-dependent.
 * The filters are how someone finds one account.
 */
export function AdminUsersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const email = params.get('email') ?? '';
  const name = params.get('name') ?? '';
  const kind = params.get('kind');
  const roleId = params.get('roleId');
  const status = statusChipOf(params);
  const can = useCan();
  const roleList = useRoleList(can('roles.manage'));
  const tabs = useUsersAndRolesTabs();

  const [emailDraft, setEmailDraft] = React.useState(email);
  const [nameDraft, setNameDraft] = React.useState(name);
  React.useEffect(() => setEmailDraft(email), [email]);
  React.useEffect(() => setNameDraft(name), [name]);

  const query = usersQueryOf(params);
  const asked = JSON.stringify(query);
  // Whether the list asks for some statuses and not the disabled one — the default, or a URL
  // naming `active` — which an empty list says, with how to see them.
  const statuses = query.status ?? [];
  const hidesDisabled = statuses.length > 0 && !statuses.includes('disabled');
  const { data, error, reload } = useAdminData<{ asked: string; page: IPage<IAdminAccountRow> }>(
    async (signal) => {
      try {
        return { asked, page: await accountsApi.listAccounts(query, signal) };
      } catch (listError) {
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [asked]
  );
  const shown = error && data?.asked !== asked ? null : data;

  /** A filter change starts at page 1: page 3 of the old query means nothing. */
  const set = (name_: string, value: Nullable<string>) => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(name_);
    else next.set(name_, value);
    next.delete('page');
    if (next.toString() === params.toString()) void reload();
    else setParams(next);
  };
  const turnTo = (target: number) => setParams(withPage(params, target));
  // Every role survey-api lists, or — without `roles.manage` — those the page drew, once each.
  const roleOptions = (
    roleList.data?.roles ??
    (shown?.page.items ?? [])
      .flatMap((account) => account.roles)
      .filter((role, index, all) => all.findIndex((other) => other.roleId === role.roleId) === index)
  ).map((role) => ({ value: role.roleId, label: role.name }));
  const narrowed = [...params.keys()].some((key) => key !== 'page');
  // The form is opened by a press, above the list (SR-439): drawn under it always, it kept the
  // list's table from ending where the window does, and the page scrolled beside the table.
  const [creating, setCreating] = React.useState(false);
  const createId = React.useId();

  return (
    <AdminPage
      title="Users"
      tabs={tabs}
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-expanded={creating}
          aria-controls={createId}
          onClick={() => setCreating((open) => !open)}
          className="gap-1.5"
        >
          <UserPlus className="size-4" aria-hidden />
          Create an account
        </Button>
      }
    >
      {/* Hidden, not unmounted: a typed address, the roles ticked and the sentence saying where
          the invitation went outlive a close. */}
      <div id={createId} hidden={!creating}>
        <CreateAccountCard onCreated={() => void reload()} />
      </div>

      <FilterBar
        search={{
          label: 'Filter by email',
          placeholder: 'Email contains…',
          draft: emailDraft,
          onDraft: setEmailDraft,
          onSubmit: () => set('email', emailDraft.trim())
        }}
        onClear={narrowed ? () => setParams(new URLSearchParams()) : undefined}
        count={shown ? `${shown.page.total} found` : undefined}
        help="The address matches on any part; a name when every word is in one participant's name. A list narrowed by both holds the accounts that match both. Disabled accounts are hidden unless Status says otherwise."
      >
        <FilterChip label="Speaks for" summary={name}>
          <form
            className="flex gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              set('name', nameDraft.trim());
            }}
          >
            <Input
              type="search"
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              placeholder="Participant name contains…"
              aria-label="Filter by participant name"
              className="w-60"
            />
            <Button type="submit" size="sm">
              Apply
            </Button>
          </form>
        </FilterChip>
        <SelectChip
          label="Kind"
          any="Every kind"
          value={kind}
          options={ACCOUNT_KINDS.map((candidate) => ({
            value: candidate,
            label: ACCOUNT_KIND_WORDS[candidate]
          }))}
          onChoose={(value) => set('kind', value)}
        />
        <SelectChip
          label="Role"
          any="Every role"
          value={roleId}
          options={roleOptions}
          onChoose={(value) => set('roleId', value)}
        />
        <SelectChip
          label="Status"
          any={STATUS_FILTER_WORDS[DEFAULT_STATUS]}
          value={status}
          options={[
            ...ACCOUNT_STATUSES.filter((candidate) => candidate !== DEFAULT_STATUS).map(
              (candidate) => ({ value: candidate, label: STATUS_FILTER_WORDS[candidate] })
            ),
            { value: EVERY_STATUS, label: 'Every status' }
          ]}
          onChoose={(value) => set('status', value)}
        />
      </FilterBar>

      {error && shown !== null && <LoadError message={error} />}

      <DataTable
        columns={ACCOUNT_COLUMNS}
        rows={shown === null || shown.page.items.length === 0 ? null : shown.page.items}
        rowKey={(account) => account.accountId}
        onOpen={(account) =>
          navigate(userPath(account.email), {
            state: { search: location.search, list: true } satisfies ICardLinkState
          })
        }
        state={
          shown === null
            ? error
              ? { kind: 'error', content: <LoadError message={error} /> }
              : { kind: 'loading' }
            : {
                kind: 'empty',
                content:
                  shown.page.total > 0 ? (
                    <PastTheEnd
                      page={shown.page.page}
                      pageSize={shown.page.pageSize}
                      total={shown.page.total}
                      counted={['account', 'accounts']}
                      onPage={turnTo}
                    />
                  ) : narrowed ? (
                    `No account matches this filter.${hidesDisabled ? DISABLED_HIDDEN : ''}`
                  ) : (
                    `No active account is held.${DISABLED_HIDDEN}`
                  )
              }
        }
        pickerId="users"
      />

      {shown && (
        <Pager
          page={shown.page.page}
          pageSize={shown.page.pageSize}
          total={shown.page.total}
          onPage={turnTo}
        />
      )}
    </AdminPage>
  );
}

/** The status a list asks for while the URL names none (SR-421): the active accounts alone. */
const DEFAULT_STATUS: AccountStatus = 'active';

/** The Status filter's word for every status — this page's, sent as no `status` at all. */
const EVERY_STATUS = 'all';

/** The Status filter's positions in words; a value the URL names outside them is drawn as
 *  spelled (`SelectChip`). */
const STATUS_FILTER_WORDS: Readonly<Record<AccountStatus, string>> = {
  active: 'Active only',
  disabled: 'Disabled only'
};

/** Said under an empty list that asks for no disabled account — the default, or `active`. */
const DISABLED_HIDDEN = ' Disabled accounts are hidden — set Status to Every status to see them.';

/** The statuses the list asks for: the default where the URL names none, none for every status,
 *  else as the URL spells them — an unknown one is survey-api's to refuse. */
function statusesOf(params: URLSearchParams): AccountStatus[] {
  const named = params.getAll('status');
  if (named.length === 0) return [DEFAULT_STATUS];
  if (named.length === 1 && named[0] === EVERY_STATUS) return [];
  return named as AccountStatus[];
}

/** The Status chip's position: the default where the URL names none or `active` alone — the
 *  default spelled out — else every value the URL names, as spelled. */
function statusChipOf(params: URLSearchParams): Nullable<string> {
  const named = params.getAll('status');
  if (named.length === 0 || (named.length === 1 && named[0] === DEFAULT_STATUS)) return null;
  return named.join(', ');
}

/** The URL's filters as the account list's query — one function for the list and its pane. */
export function usersQueryOf(
  params: URLSearchParams,
  page = pageOf(params.get('page'))
): IAccountQuery {
  return {
    email: params.get('email') ?? undefined,
    name: params.get('name') ?? undefined,
    // Sent as the URL spells them: an unknown one is survey-api's to refuse.
    kind: params.getAll('kind') as AccountKind[],
    roleId: params.getAll('roleId'),
    status: statusesOf(params),
    page
  };
}

/** Users and an account's card, as one area: the list, or a card beside it. */
export function AdminUsersArea() {
  return (
    <ListArea
      param="email"
      list={TEAM_PATHS.users}
      paneTitle="Users"
      pane={(search, selected) => <UsersPane search={search} selected={selected} />}
    />
  );
}

/** The account list as the pane beside a card draws it: the address, who it speaks for. The
 *  route's address is React Router's, decoded, and is compared as it is. */
function UsersPane({ search, selected }: { search: string; selected: string }) {
  const { shown, pager, status } = usePaneRead(
    search,
    'No account on this list.',
    (params, page, signal) => accountsApi.listAccounts(usersQueryOf(params, page), signal)
  );
  return (
    <SplitList
      label="Users"
      selected={shown?.items.find((account) => account.email === selected)?.accountId ?? null}
      items={
        shown?.items.map((account) => ({
          key: account.accountId,
          to: userPath(account.email),
          state: { search, list: true } satisfies ICardLinkState,
          primary: account.email,
          secondary:
            account.participantNames.length === 0 ? 'Speaks for nobody' : account.participantNames.join(', '),
          trailing: <AccountKindChip kind={account.kind} />
        })) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}

/** An account's columns: every one the list drew, then the two instants the row carries. */
const ACCOUNT_COLUMNS: IDataColumn<IAdminAccountRow>[] = [
  {
    id: 'email',
    header: 'Email',
    className: 'font-medium break-all',
    cell: (account) => account.email
  },
  {
    id: 'kind',
    header: 'Kind',
    cell: (account) => <AccountKindChip kind={account.kind} />
  },
  {
    id: 'roles',
    header: 'Roles',
    cell: (account) => <AccountRoleChips kind={account.kind} roles={account.roles} />
  },
  {
    id: 'status',
    header: 'Status',
    cell: (account) => (
      <StatusChip tone={account.status === 'active' ? 'neutral' : 'danger'}>
        {account.status}
      </StatusChip>
    )
  },
  {
    id: 'speaks-for',
    header: 'Speaks for',
    optional: true,
    className: 'text-muted-foreground',
    cell: (account) =>
      account.participantNames.length === 0 ? '—' : account.participantNames.join(', ')
  },
  {
    id: 'created',
    header: 'Created',
    optional: true,
    className: 'text-xs whitespace-nowrap text-muted-foreground',
    cell: (account) => formatDateTime(account.createdAt)
  },
  {
    id: 'updated',
    header: 'Updated',
    optional: true,
    hidden: true,
    className: 'text-xs whitespace-nowrap text-muted-foreground',
    cell: (account) => formatDateTime(account.updatedAt)
  },
  {
    id: 'open',
    header: '',
    label: 'Open',
    headClassName: 'w-10',
    cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
  }
];
